<template>
  <div class="quick-add">
    <!-- Overlay -->
    <transition name="fade">
      <div v-if="isOpen" class="quick-add__overlay" @click="close"></div>
    </transition>

    <!-- Mobile + Desktop drawer -->
    <div class="quick-add__panel" :class="{ 'is-active': isOpen }" role="dialog" aria-modal="true">
      <button class="quick-add__close" @click="close" aria-label="Close">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M2 2L18 18" stroke="black" />
          <path d="M18 2L2 18" stroke="black" />
        </svg>
      </button>

      <div v-if="product" class="quick-add__content">
        <h3 class="quick-add__label">Select options</h3>

        <!-- Product images carousel -->
        <div
          v-if="productImages.length"
          :key="product.id"
          class="quick-add__images carousel"
          data-arrows
          data-pagination-per-slide
          ref="carousel">
          <div class="carousel-track" data-carousel-track>
            <div v-for="(image, imgIdx) in productImages" :key="imgIdx" class="quick-add__image">
              <img :src="image" :alt="product.title" />
            </div>
          </div>
        </div>

        <!-- Product info -->
        <div class="quick-add__info">
          <a :href="product.url" class="quick-add__title">{{ productTitle }}</a>
          <p class="quick-add__colour" v-if="productColour">{{ productColour }}</p>
          <p class="quick-add__vendor" v-if="product.vendor">{{ product.vendor }}</p>
          <p class="quick-add__price">{{ formatPrice(product.price) }}</p>
        </div>

        <!-- Size options -->
        <div class="quick-add__sizes">
          <button
            v-for="(variant, idx) in product.variants"
            :key="idx"
            type="button"
            class="quick-add__size"
            :class="{
              'is-active': selectedVariant && selectedVariant.id === variant.id,
              'sold-out': !variant.available
            }"
            :disabled="!variant.available"
            @click="selectVariant(variant)">
            {{ getVariantSize(variant) }}
          </button>
        </div>

        <!-- Add to cart -->
        <button
          type="button"
          class="quick-add__add-to-cart btn btn-full btn-secondary btn-black"
          :class="{ 'is-loading': isAdding }"
          :disabled="!selectedVariant || isAdding"
          @click="addToCart">
          {{ selectedVariant ? 'add to cart' : 'please choose a size' }}
        </button>

        <a :href="product.url" class="quick-add__view-product">view product</a>
      </div>
    </div>
  </div>
</template>

<script>
import { formatMoney } from '@shopify/theme-currency'
import { addItem } from '../../scripts/cart'

export default {
  name: 'QuickAdd',

  data() {
    return {
      isOpen: false,
      isLoading: false,
      isAdding: false,
      product: null,
      selectedVariant: null
    }
  },

  computed: {
    productTitle() {
      return this.product ? this.product.title.split(' / ')[0] : ''
    },

    productColour() {
      return this.product ? this.product.title.split(' / ')[1] || '' : ''
    },

    productImages() {
      if (!this.product || !this.product.images) return []

      const images = [...this.product.images]

      if (images.length > 1) {
        // Move the image at index 1 to the end
        const secondImage = images.splice(1, 1)[0]
        images.push(secondImage)
      }

      return images.map((src) => this.$image(src, '600x'))
    }
  },

  methods: {
    // Fetch product data from handle, open quick add drawer
    async open(handle) {
      if (!handle || this.isLoading) return

      this.isLoading = true
      this.selectedVariant = null

      try {
        const response = await fetch(`/products/${handle}.js`)
        if (!response.ok) throw new Error(`Request failed with status ${response.status}`)

        const product = await response.json()

        await this.preloadImages(product.images)

        this.product = product
        this.isOpen = true
        this.initCarousel()
      } catch (error) {
        console.error('Quick add: unable to load product', error)
      } finally {
        this.isLoading = false
      }
    },

    close() {
      this.isOpen = false
      this.selectedVariant = null
    },

    selectVariant(variant) {
      if (variant.available) this.selectedVariant = variant
    },

    getVariantSize(variant) {
      const parts = variant.title.split(' / ')
      return parts.length > 1 ? parts[1] : parts[0]
    },

    formatPrice(price) {
      return formatMoney(price, theme.moneyFormat)
    },

    initCarousel() {
      this.$nextTick(() => {
        const carouselEl = this.$refs.carousel
        if (!carouselEl || typeof ScrollCarousel === 'undefined') return

        // Don't init carousel when re-opening same product - reset scroll position instead
        if (carouselEl.classList.contains('carousel-initialized')) {
          const track = carouselEl.querySelector('[data-carousel-track]')
          if (track) track.scrollLeft = 0
          return
        }

        new ScrollCarousel(carouselEl).init()
      })
    },

    preloadImages(images) {
      return Promise.all(
        images.map((src) => {
          return new Promise((resolve) => {
            const img = new Image()
            img.onload = resolve
            img.onerror = resolve
            img.src = this.$image(src, '600x')
          })
        })
      )
    },

    async addToCart() {
      if (!this.selectedVariant) return

      this.isAdding = true

      const variantId = Number(this.selectedVariant.id)
      let properties = {}

      if (Number(this.selectedVariant.compare_at_price) > Number(this.selectedVariant.price)) {
        properties = {
          _is_sale: 'true'
        }
      }

      try {
        await addItem(variantId, {
          quantity: 1,
          properties: properties
        })

        // Update header cart count
        this.updateCartCount()

        this.close()
      } catch (error) {
        console.error('Quick add: error adding to cart', error)
        alert('Error adding product to your bag, please try again.')
      } finally {
        this.isAdding = false
      }
    },

    updateCartCount() {
      const cartCountEl = document.querySelector('.cart-item-count')
      if (cartCountEl) {
        const currentCount = parseInt(cartCountEl.textContent) || 0
        cartCountEl.textContent = currentCount + 1
        cartCountEl.parentElement.classList.remove('hidden-count')
      }
    }
  }
}
</script>
